import routes from 'sitewide-js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';

// A cold work is judged by a model first, so the answer is not always instant. Comfortably
// above DownloadRedirect::PLAN_DEADLINE (2s) plus the bucketing write and two network
// legs: abort sooner and the server can record a redirect this tab never made.
const TIMEOUT_MS = 5000;

interface AfterDownloadResponse {
  redirect: boolean;
  url?: string;
}

/**
 * Asks the server whether this reader should be sent into the paper trail for the work
 * they just downloaded. The server owns the decision — it resolves the A/B arm, and
 * enrolling the reader is what this call is for.
 *
 * @returns the URL to navigate to, or null to leave the page alone.
 */
export async function paperTrailRedirectUrl(
  workId: string | number,
): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetchWithCsrf(
      routes.api_v0_demo_day_paper_trail_after_download_path({
        subdomain_param: 'api',
      }),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ work_id: workId }),
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      return null;
    }

    const body = (await response.json()) as AfterDownloadResponse;
    return body.redirect && body.url ? body.url : null;
  } catch {
    // Fails closed. A timed-out or failed check must leave the download alone.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export default paperTrailRedirectUrl;
